import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores/FlashesStore';
import { initStore } from '@/stores/store-utils';
import { getAgentNotificationChannels } from '@/types/agents';
import type {
  Agent,
  AgentRegistryTrigger,
  CreateAgentPayload,
  TestAgentPayload,
  UpdateAgentPayload,
} from '@/types/agents';

function testSuccessDescription(
  channels: ReturnType<typeof getAgentNotificationChannels>,
) {
  const slackChannel = channels.find(
    (channel) => channel.type === 'slack_channel',
  );
  if (slackChannel) {
    return `A sample Slack message was sent to #${slackChannel.channel_name.replace(/^#/, '')}`;
  }
  return 'A sample Slack message was sent';
}

export const useAgentsStore = defineStore('Agents', () => {
  const agents = ref<Agent[]>([]);
  const availableTriggers = ref<AgentRegistryTrigger[]>([]);
  const flashesStore = useFlashesStore();

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const { data: agentsData, error: agentsError } =
        await crossbeamApi.GET('/v0.2/agents');
      if (agentsError) {
        captureException(agentsError);
        flashesStore.addErrorFlash({ message: 'Could not fetch automations' });
      } else {
        agents.value = agentsData?.data ?? [];
      }

      const { data: registryData, error: registryError } =
        await crossbeamApi.GET('/v0.2/agents-registry');
      if (registryError) {
        captureException(registryError);
        flashesStore.addErrorFlash({
          message: 'Could not load automation trigger options',
        });
        availableTriggers.value = [];
      } else {
        availableTriggers.value = registryData?.data?.triggers ?? [];
      }
    },
  );

  refresh({ useCache: true });

  function getAgentById(id: string) {
    return agents.value.find((agent) => agent.id === id);
  }

  async function createAgent(body: CreateAgentPayload) {
    const { data, error } = await crossbeamApi.POST('/v0.2/agents', {
      body,
    });

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({ message: 'Failed to create automation' });
      return;
    }

    if (data?.data) {
      agents.value.push(data.data);
    }

    return data?.data;
  }

  async function updateAgent(id: string, body: UpdateAgentPayload) {
    const { data, error } = await crossbeamApi.PATCH('/v0.2/agents/{id}', {
      body,
      params: { path: { id } },
    });

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({ message: 'Failed to update automation' });
      return;
    }

    if (data?.data) {
      const updated = data.data;
      agents.value = agents.value.map((a) => (a.id === id ? updated : a));
      flashesStore.addSuccessFlash({
        message: 'Automation saved successfully.',
      });
    }

    return data?.data;
  }

  async function testAgent(body: TestAgentPayload) {
    const { data, error, response } = await crossbeamApi.POST(
      '/v0.2/agents-test',
      { body },
    );
    const status = response.status;

    if (error) {
      const serverMessage =
        (error as { errors?: string[] })?.errors?.[0] ??
        'Failed to test automation';
      if (status !== 422) {
        captureException(error);
      }
      flashesStore.addErrorFlash({ message: serverMessage });
      return false;
    }

    const dispatchError = data?.data?.dispatch?.error;
    if (dispatchError) {
      flashesStore.addErrorFlash({ message: dispatchError });
      return false;
    }

    flashesStore.addSuccessFlash({
      message: 'Test successful',
      description: testSuccessDescription(getAgentNotificationChannels(body)),
    });
    return true;
  }

  function toggleAgent(id: string, isActive: boolean) {
    return updateAgent(id, { is_active: isActive });
  }

  async function deleteAgent(id: string) {
    const { error } = await crossbeamApi.DELETE('/v0.2/agents/{id}', {
      params: { path: { id } },
    });

    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({ message: 'Failed to delete automation' });
      return false;
    }

    agents.value = agents.value.filter((a) => a.id !== id);
    flashesStore.addSuccessFlash({
      message: 'Automation deleted successfully.',
    });
    return true;
  }

  return {
    agents,
    availableTriggers,
    initError,
    ready,
    readySync,
    running,
    refresh,
    getAgentById,
    createAgent,
    testAgent,
    updateAgent,
    toggleAgent,
    deleteAgent,
  };
});
